import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Fragment, useEffect, useState } from 'react';
import BubbleButton from './BubbleButton';
import { useBubbleDrawerTools } from './BubbleDrawer';
import BubbleOverlay from './BubbleOverlay';

export interface BubbleFormActions {
  goBack: () => void;
  goForward: () => void;
  goStart: () => void;
  goTo: (pageKey: string) => void;
}

export interface BubbleFormPage<T> {
  key: string;
  render: (actions: BubbleFormActions, resources?: T) => React.ReactNode;
}

interface BubbleFormProps<T> {
  pages: Array<BubbleFormPage<T>>;
  resources?: T;
  startAtPage?: number;
}

export interface FormPageComponentHelpfulProps<T extends string = string> {
  title?: string;
  subtitle?: string;
  pageIndex?: number;

  backTitle?: string;
  forwardTitle?: string;
  forwardTooltip?: string;

  onClose: () => void;
  onGoBack?: () => void;
  onGoForward?: () => void;

  breadcrumbs?: FormHeaderBreadcrumbs<T>[];
  onBreadcrumbClick?: (breadcrumbKey: T) => void;
}

export default function BubbleForm<T>(props: BubbleFormProps<T>) {
  const firstPage = 0;

  const [currentPage, setCurrentPage] = useState(-1);

  useEffect(() => {
    setCurrentPage(props.startAtPage || firstPage);
  }, [props.startAtPage]);

  function goBack() {
    setCurrentPage(Math.max(currentPage - 1, firstPage));
  }

  function goForward() {
    setCurrentPage(Math.min(currentPage + 1, props.pages.length - 1));
  }

  function goStart() {
    setCurrentPage(props.startAtPage || firstPage);
  }

  function goTo(pageKey: string) {
    const pageIndex = props.pages.findIndex(p => p.key === pageKey);
    if (pageIndex !== -1) setCurrentPage(pageIndex);
  }

  return (
    <div className="bubble-form">
      {props.pages.map((page, indx) => (
        <div key={page.key} className="form-page">
          {currentPage !== -1 &&
            currentPage === indx &&
            page.render({ goBack, goForward, goStart, goTo }, props.resources)}
        </div>
      ))}
    </div>
  );
}

interface FormActionProps {
  onGoBack?: () => void;
  onGoForward?: () => void;
  backTitle?: string;
  forwardTitle?: string;
  backDisabled?: boolean;
  forwardDisabled?: boolean;
  isBusy?: boolean;
}

export function BubbleFormActions(props: FormActionProps) {
  return (
    <div className="flex items-center">
      {props.onGoBack && (
        <BubbleOverlay loading={props.isBusy}>
          <BubbleButton
            variant="navy"
            inverse
            onClick={props.onGoBack}
            disabled={props.backDisabled}
          >
            {props.backTitle || 'Go Back'}
          </BubbleButton>
        </BubbleOverlay>
      )}
      {props.onGoForward && (
        <BubbleOverlay loading={props.isBusy}>
          <BubbleButton disabled={props.forwardDisabled} variant="navy" onClick={props.onGoForward}>
            {props.forwardTitle || 'Next'}
          </BubbleButton>
        </BubbleOverlay>
      )}
    </div>
  );
}

export interface FormHeaderBreadcrumbs<T extends string = string> {
  key: T;
  label: string;
}

interface FormHeaderProps<T extends string = string> {
  title: string;
  subtitle: string;
  breadcrumbs?: FormHeaderBreadcrumbs<T>[];
  activeBreadcrumb?: T;
  onBreadcrumbClick?: (breadcrumbKey: T) => void;
  allowBreadcrumbSkipping?: boolean;
  pageIndex?: number;
  onClose: () => void;
  appendToCloseButton?: () => React.ReactNode;
}

export function BubbleFormHeader<T extends string = string>(props: FormHeaderProps<T>) {
  const { closeDrawer: handleClose } = useBubbleDrawerTools();

  return (
    <div className="mb-5">
      <div className="flex items-start">
        <div>
          <div className="flex items-center">
            <h1
              className="text-lg text-slate-dark font-semibold"
              dangerouslySetInnerHTML={{ __html: props.title }}
            />
          </div>
          <h2 className="text-2xl text-navy">
            {props.pageIndex !== undefined ? `${props.pageIndex}. ` : ''}
            {props.subtitle}
          </h2>
        </div>
        <div className="ml-auto" />
        {props.appendToCloseButton?.()}
        <BubbleButton
          variant="navy"
          inverse
          onClick={handleClose ?? props.onClose}
          icon="xmark-large"
          iconSize="xl"
        />
      </div>
      {props.breadcrumbs && props.breadcrumbs.length > 0 && (
        <div className="mt-2 -mx-3 flex flex-wrap items-center">
          {(props.breadcrumbs ?? []).map((crumb, idx) => {
            let isLast = idx === (props.breadcrumbs ?? []).length - 1;
            let isCurrent = crumb.key === props.activeBreadcrumb;
            let isPast =
              (props.breadcrumbs ?? []).findIndex(c => c.key === props.activeBreadcrumb) > idx;

            const classes = ['px-3 py-2 font-normal'];
            if (isCurrent) classes.push('text-navy font-bold');
            else if (isPast) classes.push('text-navy');
            else classes.push('text-smoke');

            const disabled = props.allowBreadcrumbSkipping ? false : !isCurrent && !isPast;

            return (
              <Fragment key={idx}>
                {!disabled ? (
                  <BubbleButton
                    variant="smoke"
                    className="p-0-i"
                    inverse
                    onClick={() => props.onBreadcrumbClick?.(crumb.key)}
                  >
                    <div className={classes.join(' ')}>{crumb.label}</div>
                  </BubbleButton>
                ) : (
                  <div className={classes.join(' ')}>{crumb.label}</div>
                )}
                {!isLast && (
                  <FontAwesomeIcon className="text-smoke" icon="chevron-right" fixedWidth />
                )}
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface BubbleFormFooterItem {
  label: string;
  value: string | React.ReactNode | undefined;
}

interface FormFooterProps {
  items: BubbleFormFooterItem[];
}

export function BubbleFormFooter(props: React.HTMLAttributes<HTMLDivElement> & FormFooterProps) {
  return (
    <div className={props.className}>
      {props.items.map((item, index) => (
        <Fragment key={index}>
          {item.value !== undefined && (
            <div>
              <span>{item.label}: </span>
              <span className="font-semibold">{item.value}</span>
            </div>
          )}
        </Fragment>
      ))}
    </div>
  );
}
